import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, Calendar, ChevronRight, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PublicLayout } from '@/layouts/PublicLayout';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { careerPageApi, type PublicJobResponse } from '@/lib/career-page-api';

/** Format a number as Brazilian Real currency */
function formatSalary(value: number): string {
  return `R$ ${value.toLocaleString('pt-BR')}`;
}

/** Format ISO date to pt-BR locale string */
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export default function CareerJobDetailPage() {
  const { orgSlug, jobId } = useParams<{ orgSlug: string; jobId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PublicJobResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!orgSlug || !jobId) return;
    setLoading(true);
    careerPageApi
      .getPublicJob(orgSlug, jobId)
      .then((res) => {
        setData(res);
        document.title = `${res.job.title} | ${res.organization.name}`;
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [orgSlug, jobId]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !data) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground text-lg">
            Vaga não encontrada ou não está disponível.
          </p>
        </div>
      </PublicLayout>
    );
  }

  const { job, organization } = data;

  return (
    <PublicLayout primaryColor={organization.primaryColor}>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6">
          <Link
            to={`/careers/${orgSlug}`}
            className="hover:text-foreground transition-colors"
          >
            Vagas em {organization.name}
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground truncate">{job.title}</span>
        </nav>

        {/* Job Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-3">{job.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            {job.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {job.location}
              </span>
            )}
            {job.contractType && (
              <Badge variant="secondary">{job.contractType}</Badge>
            )}
            {job.salaryRange && (
              <span className="inline-flex items-center gap-1">
                <Briefcase className="h-4 w-4" />
                {formatSalary(job.salaryRange.min)} – {formatSalary(job.salaryRange.max)}
              </span>
            )}
            {job.openedAt && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(job.openedAt)}
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        {job.description && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">Descrição da vaga</h2>
            <MarkdownRenderer content={job.description} />
          </section>
        )}

        {/* Requirements */}
        {job.requirements && job.requirements.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">Requisitos</h2>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {job.requirements.map((req: string, i: number) => (
                <li key={i}>{req}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Nice to Have */}
        {job.niceToHave && job.niceToHave.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">Diferenciais</h2>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {job.niceToHave.map((item: string, i: number) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <Button
            size="lg"
            onClick={() => navigate(`/careers/${orgSlug}/${jobId}/apply`)}
            style={{ backgroundColor: 'var(--career-primary, hsl(var(--primary)))' }}
          >
            Candidatar-se
          </Button>
        </div>
      </div>
    </PublicLayout>
  );
}
